import { forwardRef, useEffect, useMemo, useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from 'react';
import { ADMIN_ROOT, subTabsAt } from './adminRegistry';

/**
 * The Admin Panel's position lives in the URL path (/admin/<section>/<setting>/…),
 * so a refresh keeps it, Back/Forward walk through it, a setting can be linked
 * to, and every breadcrumb level is a real destination.
 *
 * Deliberately a tiny store over the History API rather than React Router:
 * the rest of the app still switches modules by state (App.tsx's `mod`), and
 * adopting a router app-wide is a separate decision
 * (docs/design/admin-panel/Admin_Panel_Wireframe.html, F1). The server
 * already answers any non-API path with index.html (server.js's SPA
 * catch-all), and Vite's dev server does the same.
 */

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function subscribe(listener: () => void) {
  listeners.add(listener);
  if (listeners.size === 1) window.addEventListener('popstate', emit);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener('popstate', emit);
  };
}

const getPath = () => window.location.pathname;

export function isAdminPath(path: string = window.location.pathname): boolean {
  return path === ADMIN_ROOT || path.startsWith(`${ADMIN_ROOT}/`);
}

export interface AdminRoute {
  /** null on the Admin home page. */
  section: string | null;
  setting: string | null;
  /** Anything below the setting — its tab, then sub-tab (ADMIN_SUBTABS). */
  rest: string[];
}

export function parseAdminPath(path: string): AdminRoute {
  if (!isAdminPath(path)) return { section: null, setting: null, rest: [] };
  const parts = path.slice(ADMIN_ROOT.length).split('/').filter(Boolean).map(decodeURIComponent);
  return { section: parts[0] ?? null, setting: parts[1] ?? null, rest: parts.slice(2) };
}

/** Moves within (or into) the Admin Panel. A no-op if already there. */
export function navigateAdmin(path: string, opts: { replace?: boolean } = {}) {
  if (path === window.location.pathname) return;
  const state = { hmsMod: 'admin-panel' };
  if (opts.replace) window.history.replaceState(state, '', path);
  else window.history.pushState(state, '', path);
  emit();
}

/** For App.tsx: after it changes the path itself (leaving the Admin Panel). */
export const notifyPathChange = emit;

export function useAdminPath(): string {
  return useSyncExternalStore(subscribe, getPath);
}

export function useAdminRoute(): AdminRoute {
  const path = useAdminPath();
  return useMemo(() => parseAdminPath(path), [path]);
}

/**
 * A real link to an Admin route: ordinary clicks navigate in place, while
 * Ctrl/⌘/Shift/middle-click keep the browser's open-in-new-tab behaviour —
 * which works, because the path alone restores the page.
 */
export const AdminLink = forwardRef<HTMLAnchorElement, AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }>(
  function AdminLink({ to, onClick, ...rest }, ref) {
    const handle = (e: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(e);
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      navigateAdmin(to);
    };
    return <a ref={ref} href={to} onClick={handle} {...rest} />;
  }
);

export interface AdminSubTabState {
  /** The active tab id at this level (the first allowed one when the URL names none). */
  active: string;
  /** The tabs this user may see at this level, with the path each one opens. */
  tabs: { id: string; label: string; to: string }[];
}

/**
 * The active tab of a setting at one level, read from the URL. `parents` are
 * the tab ids above this level ([] for a setting's own tabs, ['sms'] for
 * Messaging → SMS's sub-tabs); passing them explicitly, rather than reading
 * them back off the URL, keeps a child level correct during the render in
 * which its parent is still filling in a default. `allowed` narrows the list
 * (Messaging shows SMS/WhatsApp only to the matching grant).
 *
 * A URL with no tab, or one this user can't see, is corrected with
 * replaceState — the setting's bare URL never becomes a history entry of its
 * own, so Back skips straight past it.
 */
export function useAdminSubTab(settingId: string, parents: readonly string[] = [], allowed?: readonly string[]): AdminSubTabState {
  const route = useAdminRoute();
  const base = `${ADMIN_ROOT}/${route.section ?? ''}/${settingId}${parents.map((p) => `/${p}`).join('')}`;
  const all = subTabsAt(settingId, parents);
  const visible = allowed ? all.filter((t) => allowed.includes(t.id)) : all;
  const fromUrl = route.setting === settingId ? route.rest[parents.length] : undefined;
  const valid = visible.some((t) => t.id === fromUrl);
  const active = (valid ? fromUrl : visible[0]?.id) ?? '';

  useEffect(() => {
    if (!active) return;
    // Re-read the live path: a deeper level may have just corrected the URL
    // in this same commit, and this level's render-time route is now stale.
    const live = parseAdminPath(window.location.pathname);
    if (live.setting !== settingId) return;
    const current = live.rest[parents.length];
    if (current && visible.some((t) => t.id === current)) return;
    navigateAdmin(`${base}/${active}`, { replace: true });
    // `visible`/`parents` are derived each render; their ids are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, base, settingId, visible.map((t) => t.id).join(',')]);

  return { active, tabs: visible.map((t) => ({ id: t.id, label: t.label, to: `${base}/${t.id}` })) };
}
